import { useRef, useState, useCallback, ReactNode } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import styles from './LogoUploader.module.css';

const ACCEPTED_MIME = ['image/png', 'image/jpeg', 'image/svg+xml'];
const MAX_BYTES = 2 * 1024 * 1024;

interface LogoUploaderProps {
  /** URL assoluto del logo corrente (`null` se non caricato). */
  currentLogoUrl: string | null;
  /** Callback invocato con il File validato dopo drop/click. */
  onUpload: (file: File) => void;
  /** Callback rimozione logo (mostra bottone "Rimuovi" solo se currentLogoUrl). */
  onRemove: () => void;
  /** Stato pending: disabilita controlli + mostra spinner. */
  isUploading?: boolean;
  isRemoving?: boolean;
  /** Optional override label drop zone. */
  label?: ReactNode;
}

/**
 * Drop zone + preview per il logo studio. Nessuna libreria esterna:
 * native input[type=file] + drag-drop API. Validation MIME + size client-side
 * (server applica stessa validation come second line of defense).
 */
export function LogoUploader({
  currentLogoUrl,
  onUpload,
  onRemove,
  isUploading,
  isRemoving,
  label,
}: LogoUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFiles = useCallback((files: FileList | null | undefined) => {
    setLocalError(null);
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!ACCEPTED_MIME.includes(file.type)) {
      setLocalError('Formato non supportato (PNG, JPG, SVG)');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`File troppo grande (${(file.size / 1024 / 1024).toFixed(2)}MB, max 2MB)`);
      return;
    }
    onUpload(file);
  }, [onUpload]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset per consentire re-upload dello stesso file
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const openPicker = () => {
    if (isUploading || isRemoving) return;
    inputRef.current?.click();
  };

  return (
    <div className={styles.root}>
      {currentLogoUrl ? (
        <div className={styles.preview}>
          <div className={styles.previewImgWrap}>
            <img
              src={currentLogoUrl}
              alt="Logo studio corrente"
              className={styles.previewImg}
            />
          </div>
          <div className={styles.previewActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={openPicker}
              disabled={isUploading || isRemoving}
            >
              {isUploading ? (
                <>
                  <Loader2 size={14} className={styles.spin} />
                  <span>Caricamento…</span>
                </>
              ) : (
                <>
                  <Upload size={14} />
                  <span>Sostituisci</span>
                </>
              )}
            </button>
            <button
              type="button"
              className={styles.btnDanger}
              onClick={onRemove}
              disabled={isUploading || isRemoving}
            >
              {isRemoving ? (
                <>
                  <Loader2 size={14} className={styles.spin} />
                  <span>Rimozione…</span>
                </>
              ) : (
                <>
                  <X size={14} />
                  <span>Rimuovi</span>
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={openPicker}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
          aria-label="Carica logo studio"
        >
          <div className={styles.dropzoneIcon}>
            {isUploading ? <Loader2 size={26} className={styles.spin} /> : <ImageIcon size={26} />}
          </div>
          <div className={styles.dropzoneTitle}>
            {label ?? 'Trascina qui il logo'}
          </div>
          <div className={styles.dropzoneHint}>
            o clicca per selezionare · PNG, JPG, SVG · max 2 MB
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME.join(',')}
        onChange={onInputChange}
        className={styles.hiddenInput}
        aria-hidden
        tabIndex={-1}
      />
      {localError && (
        <div className={styles.error} role="alert">
          {localError}
        </div>
      )}
    </div>
  );
}
